import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2,
    Users,
    TrendingUp,
    Award,
    DollarSign,
    ChevronLeft,
    PieChart,
} from "lucide-react";
import { adminAudienceInsights } from "@/lib/api";
import { toast } from "sonner";

// Pretty labels for enum segments — kept here (not imported) so the
// admin page stays self-contained.
const LABELS = {
    user_type: {
        first_time_buyer: "First-Time Homebuyer",
        repeat_buyer: "Repeat Homebuyer",
        homeowner: "Homeowner",
        investor: "Real Estate Investor",
        agent: "Real Estate Agent",
        property_manager: "Property Manager",
        contractor: "Contractor",
        inspector: "Inspector",
        other: "Other",
        unanswered: "Unanswered",
    },
    age_range: {
        under_25: "Under 25",
        "25_34": "25–34",
        "35_44": "35–44",
        "45_54": "45–54",
        "55_64": "55–64",
        "65_plus": "65+",
        prefer_not_to_say: "Prefer not to say",
        unanswered: "Unanswered",
    },
    acquisition_source: {
        google: "Google",
        pinterest: "Pinterest",
        facebook: "Facebook",
        instagram: "Instagram",
        linkedin: "LinkedIn",
        referral: "Referral",
        agent: "Agent",
        inspector: "Inspector",
        contractor: "Contractor",
        email: "Email",
        other: "Other",
        unanswered: "Unanswered",
    },
    property_stage: {
        researching: "Just researching",
        scheduling_inspection: "Scheduling inspection",
        inspection_completed: "Inspection completed",
        under_contract: "Under contract",
        negotiating_repairs: "Negotiating repairs",
        closing_soon: "Closing soon",
        already_own: "Already own",
        unanswered: "Unanswered",
    },
    property_type: {
        single_family: "Single-family",
        condo: "Condo",
        townhome: "Townhome",
        duplex: "Duplex",
        multifamily: "Multifamily",
        commercial: "Commercial",
        other: "Other",
        unanswered: "Unanswered",
    },
    properties_owned: {
        none: "0 properties",
        one: "1 property",
        two_to_five: "2–5 properties",
        six_to_twenty: "6–20 properties",
        twenty_plus: "20+ properties",
        unanswered: "Unanswered",
    },
    audience_segment: {
        first_time_buyer: "First-Time Buyer",
        repeat_buyer: "Repeat Buyer",
        homeowner: "Homeowner",
        small_investor: "Small Investor (0-5)",
        growing_investor: "Growing Investor (6-20)",
        portfolio_investor: "Portfolio Investor (20+)",
        investor: "Investor",
        agent: "Agent",
        property_manager: "Property Manager",
        contractor: "Contractor",
        inspector: "Inspector",
        other: "Other",
        unanswered: "Unanswered",
    },
    property_state: {},  // 2-letter codes — render verbatim
};

export default function AdminAudienceInsights() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminAudienceInsights();
                if (alive) setData(r);
            } catch {
                toast.error("Couldn't load audience insights.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div className="min-h-[40vh] flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-bl-primary" />
            </div>
        );
    }
    if (!data) {
        return (
            <div className="bl-container py-10">
                <p className="text-sm text-bl-muted">Unable to load audience insights.</p>
            </div>
        );
    }

    return (
        <div
            className="bl-container py-6 space-y-6"
            data-testid="admin-audience-insights"
        >
            <header className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <Link
                        to="/admin"
                        className="text-xs text-bl-muted hover:text-bl inline-flex items-center gap-1 mb-2"
                        data-testid="ai-back-to-admin"
                    >
                        <ChevronLeft className="h-3 w-3" />
                        Back to admin
                    </Link>
                    <h1 className="font-display font-semibold text-2xl text-bl">
                        Audience insights
                    </h1>
                    <p className="text-sm text-bl-muted">
                        Lightweight demographics from the post-signup
                        onboarding survey. All AI-free — pure DB aggregation.
                    </p>
                </div>
            </header>

            {/* Overview cards */}
            <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
                data-testid="ai-overview-cards"
            >
                <KpiCard
                    icon={Users}
                    label="Total users"
                    value={data.totals.users.toLocaleString()}
                    testId="ai-kpi-total-users"
                />
                <KpiCard
                    icon={TrendingUp}
                    label="Onboarding completion"
                    value={`${data.totals.completion_rate_pct}%`}
                    subtext={`${data.totals.onboarded} of ${data.totals.users}`}
                    testId="ai-kpi-completion-rate"
                />
                <KpiCard
                    icon={Award}
                    label="Top user type"
                    value={LABELS.user_type[data.tops.user_type] || "—"}
                    testId="ai-kpi-top-user-type"
                />
                <KpiCard
                    icon={PieChart}
                    label="Top age range"
                    value={LABELS.age_range[data.tops.age_range] || "—"}
                    testId="ai-kpi-top-age-range"
                />
                <KpiCard
                    icon={DollarSign}
                    label="Top acquisition"
                    value={LABELS.acquisition_source[data.tops.acquisition_source] || "—"}
                    testId="ai-kpi-top-acquisition"
                />
            </div>

            {/* Marketing insights cards */}
            {data.marketing_insights?.length > 0 && (
                <section data-testid="ai-marketing-insights">
                    <h2 className="font-display font-semibold text-base text-bl mb-3">
                        Marketing insights
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {data.marketing_insights.map((m, i) => (
                            <div
                                key={`${m.kind}-${i}`}
                                className="bl-card p-4"
                                data-testid={`ai-insight-${m.kind}`}
                            >
                                <p className="text-sm font-medium text-bl mb-1">
                                    {m.headline}
                                </p>
                                <p className="text-xs text-bl-muted font-mono">
                                    {m.metric}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <BreakdownTable
                title="User type"
                rows={data.breakdowns.user_type}
                labelMap={LABELS.user_type}
                testId="ai-breakdown-user-type"
            />
            <BreakdownTable
                title="Age range"
                rows={data.breakdowns.age_range}
                labelMap={LABELS.age_range}
                testId="ai-breakdown-age-range"
            />
            <BreakdownTable
                title="Acquisition source"
                rows={data.breakdowns.acquisition_source}
                labelMap={LABELS.acquisition_source}
                testId="ai-breakdown-acquisition-source"
            />
            <BreakdownTable
                title="Property stage"
                rows={data.breakdowns.property_stage}
                labelMap={LABELS.property_stage}
                testId="ai-breakdown-property-stage"
                subtitle="Higher-intent stages (under contract, closing soon) typically convert at multiples of researching."
            />
            <BreakdownTable
                title="Property type"
                rows={data.breakdowns.property_type}
                labelMap={LABELS.property_type}
                testId="ai-breakdown-property-type"
            />
            {data.breakdowns.property_state?.length > 0 && (
                <BreakdownTable
                    title="Top states by users"
                    rows={data.breakdowns.property_state}
                    labelMap={LABELS.property_state}
                    testId="ai-breakdown-property-state"
                    subtitle="Top 15 states by user count — heatmap candidate."
                />
            )}
            <BreakdownTable
                title="Investor portfolio size"
                rows={data.breakdowns.properties_owned}
                labelMap={LABELS.properties_owned}
                testId="ai-breakdown-properties-owned"
                subtitle="Surface candidates for the Plus subscription tier."
            />
            <BreakdownTable
                title="Audience segments (derived)"
                rows={data.breakdowns.audience_segment}
                labelMap={LABELS.audience_segment}
                testId="ai-breakdown-audience-segment"
                subtitle="Auto-computed from user_type + properties_owned. No manual assignment."
            />

            {/* Conversion funnels */}
            {data.funnels?.length > 0 && (
                <section data-testid="ai-funnels-section">
                    <h2 className="font-display font-semibold text-base text-bl mb-3">
                        Conversion funnels
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        {data.funnels.map((f) => (
                            <FunnelCard
                                key={f.key}
                                funnel={f}
                                labelMap={LABELS[f.key] || {}}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function FunnelCard({ funnel, labelMap }) {
    const top = (funnel.rows || []).filter((r) => r.signup > 0).slice(0, 6);
    return (
        <section
            className="bl-card p-5"
            data-testid={`ai-funnel-${funnel.key}`}
        >
            <h3 className="font-display font-semibold text-sm text-bl mb-1">
                {funnel.label}
            </h3>
            <p className="text-[10px] uppercase tracking-[0.18em] text-bl-muted mb-3">
                Signup → Upload → Purchase → Subscribe
            </p>
            <ul className="space-y-2">
                {top.length === 0 && (
                    <li className="text-xs text-bl-muted">No data yet.</li>
                )}
                {top.map((r) => (
                    <li
                        key={r.segment}
                        className="text-xs"
                        data-testid={`ai-funnel-${funnel.key}-row-${r.segment}`}
                    >
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-bl truncate">
                                {labelMap[r.segment] || r.segment}
                            </span>
                            <span className="text-bl-muted font-mono">
                                {r.signup.toLocaleString()} signups
                            </span>
                        </div>
                        <div className="flex items-center gap-0.5 text-[10px] font-mono">
                            <FunnelStep pct={100} label="100%" tone="bl" />
                            <FunnelArrow />
                            <FunnelStep
                                pct={r.upload_pct}
                                label={`${r.upload_pct}%`}
                                tone="amber"
                            />
                            <FunnelArrow />
                            <FunnelStep
                                pct={r.purchase_pct}
                                label={`${r.purchase_pct}%`}
                                tone="green"
                            />
                            <FunnelArrow />
                            <FunnelStep
                                pct={r.subscription_pct}
                                label={`${r.subscription_pct}%`}
                                tone="primary"
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function FunnelStep({ pct, label, tone }) {
    const colorMap = {
        bl: "bg-bl-bg border-bl/40 text-bl-muted",
        amber: "bg-[var(--bl-amber-tint)] border-[var(--bl-amber)]/40 text-[var(--bl-amber)]",
        green: "bg-[var(--bl-green-soft)] border-[var(--bl-green)]/40 text-[var(--bl-green)]",
        primary: "bg-[var(--bl-teal-tint)] border-bl-primary/40 text-bl-primary",
    };
    return (
        <span
            className={
                "flex-1 px-1.5 py-0.5 rounded border text-center "
                + (colorMap[tone] || colorMap.bl)
            }
            title={`${pct}%`}
        >
            {label}
        </span>
    );
}

function FunnelArrow() {
    return <span className="text-bl-muted">›</span>;
}

function KpiCard({ icon: Icon, label, value, subtext, testId }) {
    return (
        <div className="bl-card p-4 text-center" data-testid={testId}>
            <Icon className="h-4 w-4 text-bl-muted mx-auto mb-2" />
            <div className="font-display font-semibold text-xl text-bl truncate">
                {value}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                {label}
            </div>
            {subtext && (
                <div className="mt-1 text-[11px] text-bl-muted font-mono">
                    {subtext}
                </div>
            )}
        </div>
    );
}

function BreakdownTable({ title, rows, labelMap, testId }) {
    return (
        <section className="bl-card p-5" data-testid={testId}>
            <h2 className="font-display font-semibold text-base text-bl mb-3">
                {title}
            </h2>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-bl-muted border-b border-bl/40">
                            <th className="py-2 pr-3 font-medium">Segment</th>
                            <th className="py-2 pr-3 font-medium text-right">Users</th>
                            <th className="py-2 pr-3 font-medium text-right">Share</th>
                            <th className="py-2 pr-3 font-medium text-right">Upload</th>
                            <th className="py-2 pr-3 font-medium text-right">Purchase</th>
                            <th className="py-2 pr-3 font-medium text-right">Subs</th>
                            <th className="py-2 pr-3 font-medium text-right">ARPU</th>
                            <th className="py-2 pr-0 font-medium text-right">Revenue</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr
                                key={r.segment}
                                className="border-b border-bl/20 last:border-0"
                                data-testid={`${testId}-row-${r.segment}`}
                            >
                                <td className="py-2 pr-3 text-bl font-medium">
                                    {labelMap[r.segment] || r.segment}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-bl">
                                    {r.user_count.toLocaleString()}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-bl-muted">
                                    {r.pct}%
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-bl-muted">
                                    {r.upload_rate}%
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-bl-muted">
                                    {r.purchase_rate}%
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-bl-muted">
                                    {r.sub_rate}%
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-bl">
                                    ${r.arpu.toFixed(2)}
                                </td>
                                <td className="py-2 pr-0 text-right font-mono text-bl">
                                    ${r.revenue_total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
